import React, { useState, useCallback } from 'react';
import { ActionIcon, Popover, Tooltip } from '@mantine/core';
import IconPicker from './IconPicker';

// Import all the icons we need to match IconPicker component
import {
  // Search and UI icons
  Search, X, Grid3X3,
  // CRM related icons
  Users, User, UserPlus, Building, Phone, Calendar, Bell, BellRing,
  // Document icons
  FileText, Files, ClipboardList, ClipboardCheck, FileSpreadsheet, 
  // Business icons
  Briefcase, BarChart, PieChart, TrendingUp, DollarSign, CreditCard, Wallet,
  // Communication icons
  MessageSquare, MessageCircle, Mail, Send, Share, 
  // Settings and tools
  Settings, Sliders, Filter, 
  // Status icons
  CheckCircle, XCircle, AlertCircle, Clock, Activity,
  // Other useful icons
  Star, Heart, Bookmark, Tag, Link, Globe, Image
} from 'lucide-react';

type ViewIconProps = {
  selectedIcon?: { type: 'emoji' | 'built_in', value: string };
  onSelect: (value: { type: 'emoji' | 'built_in', value: string }) => void;
  size?: number;
  className?: string;
  disabled?: boolean;
};

const ViewIcon: React.FC<ViewIconProps> = ({
  selectedIcon,
  onSelect,
  size = 16, // Reduced default size
  className = '',
  disabled = false,
}) => {
  const [opened, setOpened] = useState(false);

  const resolveIconName = (rawName?: string) => {
    if (!rawName) return undefined;
    const normalizedInput = rawName.trim();

    const aliasMap: Record<string, string> = {
      grid: "Grid3X3",
      grid3x3: "Grid3X3",
      activity: "Activity",
      "trending-up": "TrendingUp",
      "alert-circle": "AlertCircle",
      "user-plus": "UserPlus",
    };
    const alias = aliasMap[normalizedInput.toLowerCase()];
    if (alias) return alias;

    return normalizedInput
      .replace(/[-_\s]+(.)?/g, (_, ch) => (ch ? ch.toUpperCase() : ""))
      .replace(/^(.)/, (ch) => ch.toUpperCase());
  };
  
  // Handle the icon selection with proper callback
  const handleSelect = useCallback((value: { type: 'emoji' | 'built_in', value: string }) => {
    if (disabled) return;
    
    // Check if the selected icon is different from the current one
    const isSameIcon = selectedIcon && 
      selectedIcon.type === value.type && 
      selectedIcon.value === value.value;
          
    if (!isSameIcon) {
      // Only update if there's a change
      onSelect(value);
    }
    
    setOpened(false);
  }, [disabled, onSelect, selectedIcon]);

  const renderSelectedIcon = () => {
    if (!selectedIcon || !selectedIcon.value) {
      // Improved placeholder with a hint to add an icon - matching CreateView
      return (
        <div className="flex items-center justify-center w-full h-full text-gray-900 p-0 rounded-md">
          <Building size={size * 0.8} strokeWidth={1.2} />
        </div>
      );
    }

    if (selectedIcon.type === 'emoji') {
      // For emoji, we just render the Unicode character directly
      return (
        <div className="flex items-center justify-center" style={{ fontSize: size * 0.9 }}>
          {selectedIcon.value}
        </div>
      );
    }

    if (selectedIcon.type === 'built_in') {
      // Map of available icons for the app - must match all icons in IconPicker component
      // Using any type to avoid TypeScript issues with Lucide icon types
      const iconMap: Record<string, any> = {
        // Search and UI icons
        Search,
        X,
        Grid3X3,
        // CRM related icons
        Users,
        User,
        UserPlus,
        Building,
        Phone,
        Calendar,
        Bell,
        BellRing,
        // Document icons
        FileText,
        Files,
        ClipboardList,
        ClipboardCheck,
        FileSpreadsheet,
        // Business icons
        Briefcase,
        BarChart,
        PieChart,
        TrendingUp,
        DollarSign,
        CreditCard,
        Wallet,
        // Communication icons
        MessageSquare,
        MessageCircle,
        Mail,
        Send,
        Share,
        // Settings and tools
        Settings,
        Sliders,
        Filter,
        // Status icons
        CheckCircle,
        XCircle,
        AlertCircle,
        Clock,
        Activity,
        // Other useful icons
        Star,
        Heart,
        Bookmark,
        Tag,
        Link,
        Globe,
        Image
      };
      
      const resolvedName = resolveIconName(selectedIcon.value);
      const IconComponent = resolvedName ? iconMap[resolvedName] : null;
      return IconComponent ? 
        <IconComponent width={size} height={size} strokeWidth={1.2} /> : 
        <div className="flex items-center justify-center text-gray-400">
          <Image size={size} strokeWidth={1.2} />
        </div>;
    }

    return null;
  };

  // The button part of the icon picker
  const iconButton = (
    <ActionIcon
      onClick={(e) => {
        // Stop propagation to prevent the modal's outside click handler from firing
        e.stopPropagation();
        if (!disabled) {
          setOpened((o) => !o);
        }
      }}
      className={`border bg-gray-50 border-none p-0 ${disabled ? 'cursor-not-allowed opacity-70 hover:bg-gray-200' : 'hover:bg-gray-200'} rounded-md transition-colors duration-200 ${className}`}
      size={size + 4} // Minimal padding
      styles={{ 
        root: { 
          height: size + 4, 
          width: size + 4, 
          minHeight: 'unset', 
          minWidth: 'unset',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        } 
      }}
    >
      {renderSelectedIcon()}
    </ActionIcon>
  );
  
  return (
    <Popover
      opened={opened && !disabled}
      onChange={setOpened}
      position="bottom-start"
      width={320}
      shadow="md"
      withinPortal // Use portal to prevent z-index issues
      closeOnClickOutside
      styles={{
        dropdown: { zIndex: 1100 }
      }}
    >
      <Popover.Target>
        {disabled ? (
          <Tooltip label="This view cannot be modified" disabled={!disabled} position="top">
            <div onClick={(e) => e.stopPropagation()}>{iconButton}</div>
          </Tooltip>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            {iconButton}
          </div>
        )}
      </Popover.Target>

      <Popover.Dropdown p={0} className="border-0" onClick={(e) => e.stopPropagation()}>
        <IconPicker
          selectedIcon={selectedIcon}
          onSelect={handleSelect}
          onClose={() => setOpened(false)}
          isOpen
        />
      </Popover.Dropdown>
    </Popover>
  );
};

export default ViewIcon;
